import { Suspense } from "react"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowDown, ArrowUp, DotsSixVertical } from "@phosphor-icons/react"
import { graphql } from "relay-runtime"
import { Button } from "@/components/ui/button"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable"
import { useSharedMedicalRecordNoteStore } from "@/modules/patients/stores/useSharedMedicalRecordNoteStore"
import { waitingRoomPreview_waitingRoomAppointment$key } from "@/modules/waiting-room/components/__generated__/waitingRoomPreview_waitingRoomAppointment.graphql"
import { AppointmentDrawerChecker } from "@/modules/waiting-room/components/appointment-drawer/appointment-drawer-checker"
import {
  useAppointmentContext,
  useSelectNextAppointment,
  useSelectPreviousAppointment,
} from "@/modules/waiting-room/contexts/appointment-context"
import { useWaitingRoomDrawerQuery } from "@/modules/waiting-room/services/useWaitingRoomDrawerQuery"

const WaitingRoomAppointmentPreview = graphql`
  fragment waitingRoomPreview_waitingRoomAppointment on WaitingRoomAppointment {
    appointment {
      patient {
        id
        country
      }
    }
  }
`

const MedicalRecordPreview = ({ selectedWaitingRoomAppointmentId }: { selectedWaitingRoomAppointmentId: string }) => {
  const waitingRoomAppointment = useWaitingRoomDrawerQuery(selectedWaitingRoomAppointmentId)
  if (!waitingRoomAppointment) {
    throw new Error("Appointment not found")
  }

  const { appointment } = useFragment<waitingRoomPreview_waitingRoomAppointment$key>(
    WaitingRoomAppointmentPreview,
    waitingRoomAppointment,
  )
  const { id: patientId, country } = appointment.patient

  return (
    <iframe
      className="h-full w-screen xl:w-full"
      width="100%"
      title="Patient preview"
      src={`${window.location.origin}/patients/${country}/${patientId}?preview`}
    />
  )
}

const WaitingRoomPreviewWrapper = () => {
  const selectedWaitingRoomAppointmentId = useAppointmentContext((state) => state.selectedWaitingRoomAppointmentId)

  const hasAppointment = !!selectedWaitingRoomAppointmentId

  return (
    hasAppointment && (
      <Suspense fallback={null}>
        <div className="grid grid-cols-1">
          <ResizablePanelGroup direction="horizontal" autoSaveId="waiting-room-medical-record-preview">
            <ResizablePanel
              id="appointment-content-panel"
              defaultSize={28}
              minSize={25}
              className="bg-background flex min-w-92.5 flex-col border-r"
              data-testid="appointment-preview-drawer">
              <AppointmentDrawerChecker selectedId={selectedWaitingRoomAppointmentId} isPreview={true} />
            </ResizablePanel>
            <ResizableHandle className="relative h-full w-px">
              <div className="bg-surface-subdued text-on-surface-subdued absolute top-0 bottom-0 -left-2 z-1 m-auto flex h-fit w-fit items-center rounded-sm px-px py-0.5">
                <DotsSixVertical size={16} weight="thin" />
              </div>
            </ResizableHandle>
            <ResizablePanel
              defaultSize={72}
              minSize={62}
              id="medical-record-preview-panel"
              className="overflow-auto! xl:overflow-hidden">
              <MedicalRecordPreview selectedWaitingRoomAppointmentId={selectedWaitingRoomAppointmentId} />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </Suspense>
    )
  )
}

const WaitingRoomPreviewHeader = () => {
  const [t] = useTranslation()
  const selectNextAppointment = useSelectNextAppointment()
  const selectPreviousAppointment = useSelectPreviousAppointment()
  const shouldBeSelectedNextAppointmentId = useAppointmentContext((state) => state.shouldBeSelectedNextAppointmentId)
  const shouldBeSelectedPreviousAppointmentId = useAppointmentContext(
    (state) => state.shouldBeSelectedPreviousAppointmentId,
  )
  const setWaitingRoomPreviewDialog = useAppointmentContext((state) => state.setWaitingRoomPreviewDialog)
  const isSharingWaitingRoomAppointment = useAppointmentContext((state) => state.waitingRoomPreviewDialog.isSharing)
  const previewedSharedMedicalRecordPatientId = useSharedMedicalRecordNoteStore(
    (state) => state.previewedSharedMedicalRecordPatientId,
  )
  const setPreviewedSharedMedicalRecordPatientId = useSharedMedicalRecordNoteStore(
    (state) => state.setPreviewedSharedMedicalRecordPatientId,
  )
  const removeSharedMedicalRecordNote = useSharedMedicalRecordNoteStore((state) => state.removeSharedMedicalRecordNote)

  return (
    <header className="border-border bg-surface flex h-16 items-center justify-between border-b px-8 py-3">
      <div className="flex shrink-0 items-center gap-2">
        <h1 className="text-xl font-bold" data-testid="page-title">
          {t("scheduling.waitingRoom.waitingRoomPreview.waitingRoomPreview")}
        </h1>
        {!isSharingWaitingRoomAppointment && (
          <div className="item animate-fade-in-left flex items-center gap-2 opacity-100">
            <Button
              title={
                shouldBeSelectedNextAppointmentId
                  ? t("scheduling.waitingRoom.waitingRoomPreview.selectNextAppointment")
                  : t("scheduling.waitingRoom.waitingRoomPreview.noNextAppointmentAvailable")
              }
              disabled={!shouldBeSelectedNextAppointmentId}
              onClick={selectNextAppointment}
              variant="circle"
              className="h-9 w-9"
              data-testid="next-appointment-preview-arrow">
              <ArrowDown />
            </Button>
            <Button
              title={
                shouldBeSelectedPreviousAppointmentId
                  ? t("scheduling.waitingRoom.waitingRoomPreview.selectPreviousAppointment")
                  : t("scheduling.waitingRoom.waitingRoomPreview.noPreviousAppointmentAvailable")
              }
              disabled={!shouldBeSelectedPreviousAppointmentId}
              onClick={selectPreviousAppointment}
              variant="circle"
              className="h-9 w-9"
              data-testid="previous-appointment-preview-arrow">
              <ArrowUp />
            </Button>
          </div>
        )}
      </div>
      <Button
        onClick={() => {
          if (previewedSharedMedicalRecordPatientId) {
            removeSharedMedicalRecordNote(previewedSharedMedicalRecordPatientId)
            setPreviewedSharedMedicalRecordPatientId(null)
          }
          setWaitingRoomPreviewDialog(false, false)
        }}
        data-testid="close-consultation-btn">
        {t("common.button.close")}
      </Button>
    </header>
  )
}

export { WaitingRoomPreviewWrapper, WaitingRoomPreviewHeader }
